/**
 * DesignStudioPreview.js — Side-by-side variation renderer.
 * Route: /design-preview
 *
 * Renders TWO transformations side-by-side with texture-mapping swatches:
 *   - anti-microbial paint variations
 *   - countertops · backsplashes · cabinet facings · sink faucets · lighting fixtures
 *
 * Materials shown as CSS-only swatches (no external image dependencies) so
 * this ships as a pure monolithic component.
 */
import React, { useState } from "react";

const NAVY = "#020A1F"; const CYAN = "#00CFCF"; const CHROME = "#A0A0A0"; const INK = "#F5F5F7";
const shell = { minHeight: "100dvh", background: NAVY, color: INK, padding: "clamp(16px,3vw,32px)", fontFamily: "'Inter', system-ui, sans-serif", boxSizing: "border-box" };
const card = { background: "rgba(255,255,255,0.03)", border: `1px solid ${CHROME}33`, borderRadius: 12, padding: "clamp(14px,2.5vw,22px)", marginBottom: 16 };

const VARIATIONS = [
  {
    id: "warm_dignified",
    name: "Warm Dignified",
    palette: {
      paint: "#e8dfd0",          // anti-microbial cream
      countertop: "#3b3a37",     // honed graphite
      backsplash: "#c9a67a",     // travertine
      cabinet: "#2b2521",        // dark walnut
      faucet: "#7a6b55",         // brushed bronze
      lighting: "#f2d494",       // warm brass
    },
  },
  {
    id: "clean_institutional",
    name: "Clean Institutional",
    palette: {
      paint: "#f5f5f0",          // anti-microbial linen
      countertop: "#d0d3d0",     // quartz mist
      backsplash: "#a8b8bf",     // slate mist
      cabinet: "#e6e4de",        // linen-shaker
      faucet: "#8b95a1",         // brushed nickel
      lighting: "#c0c8cf",       // cool chrome
    },
  },
];

const SLOTS = [
  ["paint", "Anti-Microbial Paint"],
  ["countertop", "Countertop"],
  ["backsplash", "Backsplash"],
  ["cabinet", "Cabinet Facing"],
  ["faucet", "Sink Faucet"],
  ["lighting", "Lighting Fixture"],
];

function VariationPanel({ variation }) {
  return (
    <div style={{ ...card, background: variation.palette.paint, color: NAVY, minHeight: 380 }}
      data-testid={`design-variation-${variation.id}`}>
      <div style={{ fontSize: 11, letterSpacing: "0.2em", color: NAVY, opacity: 0.6 }}>VARIATION</div>
      <h3 style={{ margin: "4px 0 12px" }}>{variation.name}</h3>
      {/* Faux room composition */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div style={{ background: variation.palette.cabinet, borderRadius: 6, height: 90 }} />
        <div style={{ background: variation.palette.countertop, borderRadius: 6, height: 90 }} />
        <div style={{ background: variation.palette.backsplash, borderRadius: 6, height: 60 }} />
        <div style={{ background: variation.palette.lighting, borderRadius: 6, height: 60 }} />
      </div>
      {/* Swatch table */}
      <div style={{ marginTop: 16, background: "rgba(255,255,255,0.5)", borderRadius: 8, padding: 10 }}>
        {SLOTS.map(([slot, label]) => (
          <div key={slot} style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0" }}>
            <div style={{ width: 22, height: 22, borderRadius: 4, background: variation.palette[slot], border: "1px solid rgba(0,0,0,0.15)" }}
              data-testid={`swatch-${variation.id}-${slot}`} />
            <div style={{ fontSize: 12, fontWeight: 600 }}>{label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DesignStudioPreview() {
  const [selected, setSelected] = useState(null);
  return (
    <div style={shell} data-testid="design-studio-shell">
      <div style={{ ...card, borderColor: `${CYAN}44` }}>
        <div style={{ fontSize: 11, letterSpacing: "0.2em", color: CYAN, marginBottom: 6 }}>
          MOOVIN FORWARD LLC · DESIGN STUDIO · SIDE-BY-SIDE PREVIEW
        </div>
        <h1 style={{ margin: 0, fontSize: "clamp(20px, 3vw, 28px)" }}>
          Anti-microbial variations before material mobilization
        </h1>
        <div style={{ color: CHROME, marginTop: 6, fontSize: 13 }}>
          Lock the variation you want before procurement fires.
        </div>
      </div>

      <div style={{ display: "grid", gap: 16, gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
        {VARIATIONS.map(v => (
          <div key={v.id}>
            <VariationPanel variation={v} />
            <button data-testid={`design-lock-${v.id}`}
              onClick={() => setSelected(v.id)}
              style={{
                width: "100%", padding: "10px 18px",
                background: selected === v.id ? CYAN : "transparent",
                color: selected === v.id ? NAVY : INK,
                border: `1px solid ${selected === v.id ? CYAN : CHROME + "55"}`,
                borderRadius: 8, fontWeight: 700, cursor: "pointer",
                letterSpacing: "0.05em", fontSize: 12, textTransform: "uppercase",
              }}>
              {selected === v.id ? "✓ Variation Locked" : "Lock this variation"}
            </button>
          </div>
        ))}
      </div>

      {selected && (
        <div style={{ ...card, borderColor: CYAN, marginTop: 20 }} data-testid="design-lock-confirmation">
          ✓ Variation <strong>{VARIATIONS.find(v => v.id === selected).name}</strong> locked. Procurement can fire when you are ready.
        </div>
      )}
    </div>
  );
}
